// @flow

import React from 'react';
import {
    Pressable, View, useWindowDimensions,
} from 'react-native';
import { FormattedMessage } from 'react-intl';
import { Draggable } from 'react-beautiful-dnd';
import {
    actionButton, actionButtonPhone,
    dragButtonContainer,
    dragButtonContainerPhone,
    tableRowEven, tableRowEvenHovered,
    tableRowEvenPhone, tableRowEvenPhoneHovered,
    tableRowOdd, tableRowOddHovered,
    tableRowOddPhone, tableRowOddPhoneHovered,
} from 'src/styles/common';
import Text from '../../components/CustomText/CustomText';
import MoveIcon from '../../Icons/MoveIcon';
import MoveUpIcon from '../../Icons/MoveUpIcon';
import MoveDownIcon from '../../Icons/MoveDownIcon';
import { pageStyles } from '../defaultPageStyles';

type Props = {
    item: Object,
    index: Number,
    addedChannels: Array,
    setAddedChannels: Function,
    setRemainingChannels: Function,
    channels: Array,
    isDefault: string,
};

export const setDivStyles = (p, index, width, hovered) => {
    const divStyles = { ...p.draggableProps.style };
    if (index % 2 === 0) {
        if (hovered) {
            if (width > 600) {
                return { ...divStyles, ...tableRowEvenHovered };
            }
            return { ...divStyles, ...tableRowEvenPhoneHovered };
        }
        if (width > 600) {
            return { ...divStyles, ...tableRowEven };
        }
        return { ...divStyles, ...tableRowEvenPhone };
    }
    if (hovered) {
        if (width > 600) {
            return { ...divStyles, ...tableRowOddHovered };
        }
        return { ...divStyles, ...tableRowOddPhoneHovered };
    }
    if (width > 600) {
        return { ...divStyles, ...tableRowOdd };
    }
    return { ...divStyles, ...tableRowOddPhone };
};

const AddedChannelItem = (props: Props) => {
    const {
        item, index, setAddedChannels, setRemainingChannels, channels,
        isDefault,
    } = props;

    const { width } = useWindowDimensions();

    const [hovered, setHovered] = React.useState(false);

    const moveUpHandler = React.useCallback(() => {
        if (index === 0) {
            return;
        }

        setAddedChannels((addedChannels) => {
            const newAddedChannels = addedChannels.filter(
                (value, i) => i !== index,
            );
            newAddedChannels.splice(index - 1, 0, item);
            return newAddedChannels;
        });
    }, [index, item, setAddedChannels]);

    const removeChannelHandler = React.useCallback(() => {
        setAddedChannels((addedChannels) => addedChannels.filter(
            (value, i) => i !== index,
        ));
        setRemainingChannels([...channels, item].sort(
            (a, b) => (a.number - b.number),
        ));
    }, [channels, index, item, setAddedChannels, setRemainingChannels]);

    const moveDownHandler = React.useCallback(() => {
        setAddedChannels((addedChannels) => {
            const newAddedChannels = addedChannels.filter(
                (value, i) => i !== index,
            );
            newAddedChannels.splice(index + 1, 0, item);
            return newAddedChannels;
        });
    }, [index, item, setAddedChannels]);

    return (
        <Draggable draggableId={item.uuid} index={index}>
            {(p) => (
                <div
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    {...p.draggableProps}
                    style={setDivStyles(p, index, width, hovered)}
                    ref={p.innerRef}
                >
                    <View
                        style={pageStyles.channel_items_container}
                    >
                        <Text style={
                            width > 600
                                ? [pageStyles.channel_items, pageStyles.channel_items_first_column]
                                : [pageStyles.channel_items, pageStyles.channel_items_first_column_phone]
                        }
                        >
                            {index + 1}
                        </Text>
                        <Text style={
                            width > 600
                                ? [pageStyles.channel_items, pageStyles.channel_items_second_column]
                                : [pageStyles.channel_items, pageStyles.channel_items_second_column_phone]
                        }
                        >
                            {item.name}
                        </Text>
                        {width > 600 && (
                            <>
                                <Text
                                    style={[pageStyles.channel_items, pageStyles.channel_items_third_column]}
                                >
                                    {item.category.name}
                                </Text>
                                <Text
                                    style={[pageStyles.channel_items, pageStyles.channel_items_fourth_column]}
                                >
                                    {item.resolution.name}
                                </Text>
                            </>
                        )}
                        {width < 600 && (
                            <div
                                {...p.dragHandleProps}
                                style={width > 600 ? dragButtonContainer : dragButtonContainerPhone}
                            >
                                <View
                                    style={actionButtonPhone}
                                >
                                    <MoveIcon />
                                </View>
                            </div>
                        )}
                        <View
                            style={width > 600
                                ? [pageStyles.channel_items, pageStyles.channel_items_fifth_column]
                                : [pageStyles.channel_items, pageStyles.channel_items_action_button_column_phone]}
                        >
                            {width > 600 && (
                                <div
                                    {...p.dragHandleProps}
                                    style={actionButton}
                                >
                                    <MoveIcon />
                                </div>
                            )}
                            <Pressable
                                style={width > 600
                                    ? [pageStyles.action_buttons, pageStyles.action_buttons_margin]
                                    : pageStyles.action_buttons}
                                onPress={moveUpHandler}
                            >
                                <MoveUpIcon />
                            </Pressable>
                            <Pressable
                                style={[pageStyles.action_buttons, pageStyles.action_buttons_margin]}
                                onPress={moveDownHandler}
                            >
                                <MoveDownIcon />
                            </Pressable>
                            {!isDefault && (
                                <Pressable
                                    style={[pageStyles.action_buttons, pageStyles.action_buttons_margin]}
                                    onPress={removeChannelHandler}
                                >
                                    <Text style={pageStyles.light_blue}>
                                        <FormattedMessage
                                            id="remove-channel"
                                            defaultMessage="Remove Channel"
                                        />
                                    </Text>
                                </Pressable>
                            )}
                        </View>
                    </View>
                </div>
            )}
        </Draggable>
    );
};

export default AddedChannelItem;
